import React, {Component, useContext} from 'react'
import PropTypes from 'prop-types'
import Draggable from 'react-draggable'
import {ReactComponent as AngleDownIcon} from '@shop/icon-fonts/optimized/angle-down-small.svg'
import AppContext from '../../context'
import CategoryFlyout from './CategoryFlyout'
import {breakpointsNumeric} from '../../theme/config'
import {AccessibleText, DEVICE_TYPES} from '../../utils'
import {
  CategoryBar,
  CategoryItem,
  CategoryItemButton,
  CategoryItemLink,
  CategoryWrapper,
  EmptyNav,
  Navigation
} from './LowerBar.styled'
import {WhiteIcon} from '../Icon/Icon.styled'

export const ENTER_DELAY = 150
export const LEAVE_DELAY = 300

class DraggableMenu extends Component {
  static contextType = AppContext

  constructor(props) {
    super(props)
    const {categories} = this.props

    this.state = {
      dragX: 0,
      leftGradient: false,
      rightGradient: false,
      flyoutsMap: new Map(categories.map((category, index) => [index, {node: React.createRef(), show: false}]))
    }
    this.onCategoryMouseEnterTimeoutID = null
    this.onCategoryMouseLeaveTimeoutID = null
  }

  componentDidMount() {
    this.checkHiddenContentOverlays()
  }

  onDrag = (event, data) => {
    event.preventDefault()
    const {x} = data
    const {dragX} = this.state
    if (x !== dragX) {
      this.setState({dragX: x})
    }
    this.checkHiddenContentOverlays()
  }

  onCategoryItemMouseEnter = (index, callback = () => {}) => {
    clearTimeout(this.onCategoryMouseLeaveTimeoutID)
    this.onCategoryMouseEnterTimeoutID = setTimeout(() => {
      const {categories} = this.props
      const entries = categories.map((category, i) => [i, {node: React.createRef(), menu: React.createRef(), show: index === i}])
      const flyoutsMap = new Map(entries)
      this.setState({flyoutsMap}, callback)
    }, ENTER_DELAY)
  }

  onCategoryFlyoutMouseEnter = () => {
    clearTimeout(this.onCategoryMouseLeaveTimeoutID)
  }

  onCategoryMouseLeave = () => {
    clearTimeout(this.onCategoryMouseEnterTimeoutID)
    this.onCategoryMouseLeaveTimeoutID = setTimeout(() => {
      const {categories} = this.props
      const entries = categories.map((category, i) => [i, {node: React.createRef(), menu: React.createRef(), show: false}])
      const flyoutsMap = new Map(entries)
      this.setState({flyoutsMap})
    }, LEAVE_DELAY)
  }

  onAccessibleButtonClick = (index) => {
    this.onCategoryItemMouseEnter(index, () => {
      const {flyoutsMap} = this.state
      const {node} = flyoutsMap.get(index)
      node.current.querySelector('a').focus()
    })
  }

  hideAllFlyouts = (callback = () => {}) => {
    const {categories} = this.props
    const entries = categories.map((category, i) => [i, {node: React.createRef(), menu: React.createRef(), show: false}])
    const flyoutsMap = new Map(entries)
    this.setState({flyoutsMap}, callback)
  }

  hideAllFlyoutsAndFocus = (index = 0, target = 'a') => {
    this.hideAllFlyouts(() => {
      const {flyoutsMap} = this.state
      const {menu} = flyoutsMap.get(index)
      const elem = menu.current.querySelector(target)
      elem.focus()
    })
  }

  checkHiddenContentOverlays() {
    const {dragX, leftGradient, rightGradient} = this.state
    const {clientWidth} = this.props
    const displayLeft = dragX < -20
    if (displayLeft && !leftGradient) {
      this.setState({leftGradient: true})
    } else if (!displayLeft && leftGradient) {
      this.setState({leftGradient: false})
    }
    const displayRight = dragX > clientWidth - breakpointsNumeric.large + 20
    if (displayRight && !rightGradient) {
      this.setState({rightGradient: true})
    } else if (!displayRight && rightGradient) {
      this.setState({rightGradient: false})
    }
  }

  render() {
    const {device, showCategories, translations} = this.props
    const hidden = device === DEVICE_TYPES.SMARTPHONE || device === DEVICE_TYPES.PHABLET
    if (hidden) {
      return null
    }

    if (!showCategories) {
      return <EmptyNav />
    }

    const {leftGradient, rightGradient} = this.state
    const {categories, clientWidth} = this.props
    const isDraggable = device === DEVICE_TYPES.TABLET || device === DEVICE_TYPES.LAPTOP
    const MENU = (
      <CategoryBar>
        {categories.map((category, index) => {
          const {flyoutsMap} = this.state
          const {show, menu} = flyoutsMap.get(index)

          const accessibleLabel = translations.get('shop.header.main.navigation.category.accessible.button.label')

          return (
            <CategoryItem
              key={index}
              ref={menu}
              aria-haspopup="true"
              aria-expanded={show}
              hovered={show}
              onMouseEnter={() => this.onCategoryItemMouseEnter(index)}
              onMouseLeave={this.onCategoryMouseLeave}
            >
              <CategoryItemLink
                href={category.path}
              >
                {category.title}
              </CategoryItemLink>
              <CategoryItemButton
                onClick={() => this.onAccessibleButtonClick(index)}
                aria-expanded={show}
              >
                <WhiteIcon>
                  <AngleDownIcon width="18" height="18" viewBox="3 3 18 18" />
                </WhiteIcon>
                <AccessibleText>
                  {`${accessibleLabel}: ${category.title}`}
                </AccessibleText>
              </CategoryItemButton>
            </CategoryItem>
          )
        })}
      </CategoryBar>
    )

    const FLYOUTS = categories.map((category, index) => {
      const {flyoutsMap} = this.state
      const {node, show} = flyoutsMap.get(index)

      const onBlurFirst = () => {
        this.hideAllFlyoutsAndFocus(index, 'button')
      }
      const onBlurLast = () => {
        const isLastFlyout = index === flyoutsMap.size - 1
        if (!isLastFlyout) {
          this.hideAllFlyoutsAndFocus(index + 1, 'a')
        } else {
          this.hideAllFlyouts()
        }
      }

      return (
        <CategoryFlyout
          key={index}
          ref={node}
          show={show}
          category={category}
          onMouseEnter={this.onCategoryFlyoutMouseEnter}
          onMouseLeave={this.onCategoryMouseLeave}
          onBlurFirst={onBlurFirst}
          onBlurLast={onBlurLast}
        />
      )
    })

    const maxLeftPosition = clientWidth - breakpointsNumeric.large

    const withDraggable = (DraggableComponent, OtherComponent) => (
      <Navigation
        aria-label={translations.get('shop.header.main.navigation.title')}
      >
        {isDraggable
          ? <CategoryWrapper leftGradient={leftGradient} rightGradient={rightGradient}>
            <Draggable
              axis="x"
              bounds={{right: 0, left: maxLeftPosition}}
              onDrag={this.onDrag}
            >
              {DraggableComponent}
            </Draggable>
          </CategoryWrapper>
          : DraggableComponent}
        {OtherComponent}
      </Navigation>
    )

    return withDraggable(MENU, FLYOUTS)
  }
}

DraggableMenu.propTypes = {
  categories: PropTypes.arrayOf(Object).isRequired,
  showCategories: PropTypes.bool.isRequired,
  clientWidth: PropTypes.number.isRequired,
  device: PropTypes.oneOf(Object.values(DEVICE_TYPES)).isRequired,
  translations: PropTypes.instanceOf(Map)
}

DraggableMenu.defaultProps = {
  translations: new Map()
}

const LowerBar = () => {
  const {categories, device, clientWidth, showCategories, translations} = useContext(AppContext)
  const props = {categories, device, clientWidth, showCategories, translations}
  return <DraggableMenu {...props} />
}


export default LowerBar
